import type { InfoResponse } from "../../types/ApiResponses";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  useTranslation,
  Td,
  Tr,
  Text,
  Table,
  Flex,
  Button,
  Modal,
  Icon,
  SingleSearchSelector,
  Spinner,
  Tag,
  Option,
  Link,
} from "@familyzone/component-library";

import CardBasedPage from "../templates/CardBasedPage";
import Api from "../../utils/Api";
import SortSearchTable from "../templates/SortSearchTable";

import { useDateRangeFilterStore } from "../../storez/DateRangeFilterStore";
import GlobalDatePickerVisibilityActions from "../../actions/GlobalDatePickerVisibilityActions";

import { AuditLog } from "../../types/QoriaAudit";
import FileSaver from "file-saver";

import { QuestionIcon } from "@chakra-ui/icons";
import { Tooltip } from "@chakra-ui/react";

const ACCESS_REASONS: Option[] = [
  { label: "Troubleshooting", value: "troubleshooting" },
  { label: "Maintenance", value: "maintenance" },
  { label: "Software Update", value: "software_update" },
  { label: "Security Audit", value: "security_audit" },
  { label: "Configuration Change", value: "configuration_change" },
  { label: "Other", value: "other" },
];

const RemoteSupportConfig: React.FC = () => {
  const { t } = useTranslation();
  const [remoteSupportEnabled, setRemoteSupportEnabled] = useState<boolean | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const hasDeviceInformation = (data: unknown): data is InfoResponse =>
    typeof data === "object" && data !== null && "device_information" in data && !!data.device_information;

  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loaded, setIsLoaded] = useState(false);
  const [accessReasonOption, setAccessReasonOption] = useState<Option | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [dates, setDates] = useState({
    start: useDateRangeFilterStore.getState().startDate,
    end: useDateRangeFilterStore.getState().endDate,
  });

  const handleOpenModal = useCallback((log: AuditLog) => {
    setSelectedLog(log);
    setIsModalOpen(true);
  }, []);

  const columns = useMemo(
    () => [
      { headerText: t("Access Time"), columnName: "startTime", sortable: true },
      { headerText: t("Appliance"), columnName: "deviceId", sortable: true },
      { headerText: t("Access Reason"), columnName: "reason", sortable: true, searchable: true },
      { headerText: t("Comments"), columnName: "comments", sortable: true, searchable: true },
      { headerText: t("Operations"), columnName: "operations" },
    ],
    [t]
  );

  const tableDataMap = useCallback(
    (record: AuditLog, index: number) => (
      <Tr key={`${record.deviceId}-${index}`}>
        <Td>
          <Text fontSize="md">{new Date(record.startTime).toLocaleString()}</Text>
        </Td>
        <Td>
          <Text fontSize="md">{record.deviceId}</Text>
        </Td>
        <Td>
          <Text fontSize="md">{ACCESS_REASONS.find((opt) => opt.value === record.reason)?.label || record.reason}</Text>
        </Td>
        <Td>
          <Text fontSize="md">
            {record.comments.substring(0, 40)}
            {record.comments.length > 40 ? "..." : ""}
          </Text>
        </Td>
        <Td>
          <Button variant="secondary" size="sm" onClick={() => handleOpenModal(record)} aria-label={t("View audit log details")}>
            <Icon icon="fa-eye" data-testid="view-log-icon" variant="solid" color="text.paragraph.light" />
          </Button>
        </Td>
      </Tr>
    ),
    [handleOpenModal, t]
  );

  useEffect(() => {
    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      GlobalDatePickerVisibilityActions.showGlobalDatePicker();
    }, 0);

    const unsubscribe = useDateRangeFilterStore.subscribe((state) => {
      setDates({ start: state.startDate, end: state.endDate });
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    setIsLoaded(false);
    const payload = {
      reason: accessReasonOption?.value,
      startDate: dates.start,
      endDate: dates.end,
    };

    Api.post(
      "/ajax/diags/remote_audit_logs",
      payload,
      (result: AuditLog[] | { status: string }) => {
        if (Array.isArray(result)) {
          setAuditLogs(result);
        } else {
          setAuditLogs([]);
          console.warn("Audit Log couldn't be loaded:", result.status);
        }
        setIsLoaded(true);
      },
      () => setIsLoaded(true)
    );
  }, [accessReasonOption, dates]);

  const handleDownloadCSV = () => {
    if (!auditLogs.length) return;

    const headers = columns
      .filter((col) => col.columnName !== "operations") // Exclude UI-only columns
      .map((col) => col.headerText);
    const rows = auditLogs.map((log) => {
      const reasonLabel = ACCESS_REASONS.find((opt) => opt.value === log.reason)?.label ?? log.reason;

      return [log.startTime, log.deviceId, reasonLabel, `"${(log.comments || "").toString().replace(/"/g, '""')}"`].join(",");
    });
    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    FileSaver.saveAs(blob, `audit_logs_${new Date().toISOString().split("T")[0]}.csv`);
  };

  useEffect(() => {
    Api.get(
      "/info",
      (result: unknown) => {
        if (hasDeviceInformation(result)) {
          const enabled = result.device_information.remote_support_enabled;
          setRemoteSupportEnabled(typeof enabled === "boolean" ? enabled : undefined);
        } else {
          setRemoteSupportEnabled(undefined);
        }
        setIsLoading(false);
      },
      () => {
        setRemoteSupportEnabled(undefined);
        setIsLoading(false);
      }
    );
  }, []);

  const tagLabel = remoteSupportEnabled === true ? t("Enabled") : remoteSupportEnabled === false ? t("Disabled") : t("Not Supported");

  const tagVariant = remoteSupportEnabled === true ? "green" : remoteSupportEnabled === false ? "red" : "dark";

  return (
    <Box bg="gray.50" minH="100vh" px="sp24" py="sp24">
      {/* Box 1 — Status card */}
      <CardBasedPage
        title={t("Remote Support Config")}
        breadcrumbs={[
          { title: t("Debugging"), url: "/managedevice", isActive: false },
          { title: t("Diagnostics"), url: "/managedevice/settings/diagnostics", isActive: false },
          { title: t("Remote Support Config"), isActive: true },
        ]}
      >
        <Box p="sp16">
          <Flex direction="column" gap="sp8">
            <Box fontSize="lg">
              <Flex alignItems="center" gap="sp8">
                <Box>{t("Remote Support Status:")}</Box>
                {isLoading && <Spinner size="sm" />}
                {!isLoading && <Tag size="default" variant={tagVariant} tagLabel={tagLabel} />}
              </Flex>
            </Box>
            <Flex color="neutrals.200" alignItems="center">
              <Text>{t("Use the appliance CLI to enable or disable remote support access.")}</Text>
              <Tooltip variant="dark" hasArrow maxWidth="336px" fontSize="sm" label={t("Click for help")}>
                <Link
                  isExternal
                  href="https://help.linewize.com/hc/en-gb/articles/28545724467868"
                  color="currentColor"
                  aria-label={t("Click for help")}
                  ml={1}
                  alignItems="center"
                  display="flex"
                >
                  <QuestionIcon boxSize={4} />
                </Link>
              </Tooltip>
            </Flex>
          </Flex>
        </Box>
      </CardBasedPage>

      {/* Box 2 — Audit log card */}
      <Box mt="sp24">
        <CardBasedPage>
          <Box p="sp16">
            <Box mb="sp16">
              <Text fontSize="lg" fontWeight="bold" color="neutrals.900">
                {t("Remote Support Audit Log")}
              </Text>
              <Text fontSize="sm" color="neutrals.200" mt="sp4">
                {t("A record of every remote support session accessed for this appliance.")}
              </Text>
            </Box>

            <SortSearchTable
              columns={columns}
              loaded={loaded}
              data={auditLogs}
              tableDataMap={tableDataMap}
              tableMargin="0"
              childrenInTableHeader={
                <Flex gridGap="sp12" alignItems="center">
                  <Box width="250px">
                    <SingleSearchSelector
                      id="reason-selector"
                      name="reason-selector"
                      options={ACCESS_REASONS}
                      selected={accessReasonOption}
                      placeholder={t("Filter by Reason")}
                      onChange={setAccessReasonOption}
                      showClearIcon={true}
                      disabled={!loaded}
                    />
                  </Box>
                  <Button
                    variant="primary"
                    disabled={!loaded || !auditLogs.length}
                    onClick={handleDownloadCSV}
                    leftIcon={<Icon icon="fa-file-csv" variant="solid" />}
                  >
                    {t("Export CSV")}
                  </Button>
                </Flex>
              }
            />
          </Box>
        </CardBasedPage>
      </Box>

      <AuditLogModal open={isModalOpen} onClose={() => setIsModalOpen(false)} log={selectedLog} />
    </Box>
  );
};

export default RemoteSupportConfig;

interface AuditLogModalProps {
  open: boolean;
  onClose: () => void;
  log: AuditLog | null; // Pass the selected data here
}

const AuditLogModal: React.FC<AuditLogModalProps> = ({ open, onClose, log }) => {
  const { t } = useTranslation();

  // Return the difference between first/last log  in mm:ss format
  // (returns 00:00 if there's just the one log) - it's not perfect
  // but we don't know the real length of a session, just when commands
  // were executed
  const getDuration = () => {
    if (log === null) {
      //should never get here but returning an empty string makes most sense
      return "";
    }
    const s = new Date(log.startTime);
    const e = new Date(log.endTime);
    const totalSeconds = Math.floor(Math.abs(e.getTime() - s.getTime()) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const paddedSeconds = seconds.toString().padStart(2, "0");
    return `${minutes}m:${paddedSeconds}s`;
  };

  return (
    <Modal isOpen={open} onClose={onClose} headerText={t("Session Details")} size="md" contentProps={{ style: { overflow: "visible" } }}>
      {log ? (
        <>
          <Box my="sp24" overflowY="auto" py="sp4" overflow={"visible"}>
            <Table style={{ tableLayout: "fixed" }}>
              <Tr>
                <Td width="160px">
                  <Text fontSize="medium" color="text.title">
                    {t("Access Time")}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="medium" color="text.title">
                    {new Date(log.startTime).toLocaleString()}
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td width="160px">
                  <Text fontSize="medium" color="text.title">
                    {t("Appliance")}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="medium" color="text.title">
                    {log.deviceId}
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td width="160px">
                  <Text fontSize="medium" color="text.title">
                    {t("Access Reason")}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="medium" color="text.title">
                    {ACCESS_REASONS.find((opt) => opt.value === log.reason)?.label ?? log.reason}
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td width="160px">
                  <Text fontSize="medium" color="text.title">
                    {t("Duration")}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="medium" color="text.title">
                    {getDuration()}
                  </Text>
                </Td>
              </Tr>
              <Tr>
                <Td width="160px">
                  <Text fontSize="medium" color="text.title">
                    {t("Comments")}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="medium" color="text.title">
                    {log.comments}
                  </Text>
                </Td>
              </Tr>
            </Table>
          </Box>
          <Flex>
            <Button onClick={onClose} variant="primary" aria-label="close">
              {t("Close")}
            </Button>
          </Flex>
        </>
      ) : (
        // Should never see this but otherwise React complains
        <Text p="sp24">{t("Loading...")}</Text>
      )}
    </Modal>
  );
};
